// Customer-facing name for a coach's class_type 'group' sessions.
//
// The admin team sets it per coach+sport in `coach_sports.group_label` (Coaches
// page → Pricing), so a coach who sells "Match Coach" and one who sells
// "Group Class" can sit side by side. Unset falls back to the sport default —
// Padel reads "Match Coach", everything else "Group Class" — which is what keeps
// a newly added Padel coach correct without the admin typing anything.
//
// DISPLAY ONLY — the stored `class_type` stays 'group' on every path (the
// coaching_booking_price RPC branches on it, booking_metadata carries it, and
// delete-coaching-booking reconstructs the group block from it). Renaming the
// value instead of the label would silently reprice and un-group live bookings.
// Every customer-facing "group class" string goes through this helper so the
// form and the checkout can never disagree.
export const coachingGroupLabelDefault = (sportName) =>
  sportName === "Padel" ? "Match Coach" : "Group Class";

export const coachingGroupLabel = (customLabel, sportName) =>
  (typeof customLabel === "string" && customLabel.trim()) ||
  coachingGroupLabelDefault(sportName);

// Malaysia SST (Sales & Service Tax) rate applied to booking totals in the checkout UI.
// Single source of truth — if the SST rate changes, update it here and every checkout's
// tax line + label follow automatically.
export const SST_RATE = 0.08;
export const SST_LABEL = `SST(${Math.round(SST_RATE * 100)}%)`;

// Self-service cancellation window, mirrored from process-refund-as-credits-v7.
// THIS IS UX ONLY — it decides whether the cancel button is enabled and what the
// tooltip says. The edge function is the sole authority: it re-derives the
// caller, the ownership and the coach role from the JWT and the database on
// every request, and ignores anything the browser sends beyond the bookingId.
// Keep the numbers and the boundary (`<=` rejects) in step with that function,
// or the button will enable a call the server then refuses.
export const REFUND_WINDOW_HOURS = 48;
export const COACH_REFUND_WINDOW_HOURS = 6;

// A user counts as a coach here only if they hold at least one coach_sports row
// — a TEACHING coach — which is what the server checks. The 'coach'
// membership_status tier is a pricing/priority-queue tier and does NOT grant the
// short window; pass it in and the button would enable a call the server refuses.
export const refundWindowHours = (isCoach) =>
  isCoach ? COACH_REFUND_WINDOW_HOURS : REFUND_WINDOW_HOURS;

export const refundWindowMessage = (hours) =>
  `Cannot refund within ${hours} hours of booking start time.`;

// Hours from now until a booking starts, anchored to Malaysia time (+08:00)
// exactly as the edge function does. The other client parsers build browser-local
// Dates; at a 6-hour window a device on the wrong timezone would otherwise show
// an enabled button for a booking the server considers closed.
// `date` is "DD/MM/YYYY"; `time` is "HH:MM AM" or a "HH:MM AM - HH:MM AM" range.
//
// nextDayEarlyAM mirrors process-refund-as-credits-v7's bookingStartInstant: the
// venue runs 06:00 -> 02:00, so 12/01/02 AM slots start the day AFTER the stored
// booking_date. Pass false ONLY to match a server that does NOT apply the shift
// (delete-coaching-booking still parses the raw date) — matching the server that
// will actually answer the call matters more than being independently right,
// because a disagreement shows an enabled button that then 400s.
export function hoursUntilBookingMYT(date, time, { nextDayEarlyAM = true } = {}) {
  const [day, month, year] = String(date).split("/").map(Number);
  const startTime = String(time).split(" - ")[0];
  const [hourStr, minuteStr, period] = startTime.split(/[: ]/);
  let hour = parseInt(hourStr, 10);
  if (period === "PM" && hour !== 12) hour += 12;
  if (period === "AM" && hour === 12) hour = 0;

  // Normalise through a Date so month/year rollover is handled (31st + 1 day).
  let y = year;
  let m = month;
  let d = day;
  if (nextDayEarlyAM && period === "AM" && hour >= 0 && hour < 3) {
    const shifted = new Date(year, month - 1, day + 1);
    y = shifted.getFullYear();
    m = shifted.getMonth() + 1;
    d = shifted.getDate();
  }

  const pad = (n) => String(n).padStart(2, "0");
  const bookingStart = new Date(
    `${y}-${pad(m)}-${pad(d)}T${pad(hour)}:${pad(parseInt(minuteStr, 10))}:00+08:00`
  );
  return (bookingStart.getTime() - Date.now()) / (1000 * 60 * 60);
}

// Today's calendar date in Malaysia (UTC+8, no DST) as "YYYY-MM-DD", without
// consulting the browser's timezone: shift the instant by +8h and read the UTC
// date — the same +08:00 anchoring hoursUntilBookingMYT uses. A customer on a
// device set to another zone would otherwise ask the server for "today" one
// day off around midnight. `offsetDays` walks Malaysia calendar days
// (mytTodayISO(1) = tomorrow); the sum stays exact because UTC has no DST.
export const MYT_OFFSET_MS = 8 * 60 * 60 * 1000;

export function mytTodayISO(offsetDays = 0) {
  return new Date(Date.now() + MYT_OFFSET_MS + offsetDays * 24 * 60 * 60 * 1000)
    .toISOString()
    .slice(0, 10);
}
